const MAX_SELECTED_TAGS = 3;

/**
 * Validates whether a pill selection should be allowed based on filtering state and tag limits
 * @param libraryElement - The library element to check
 * @param pillElement - The pill element being selected
 * @param isSelected - Whether the pill is being selected (true) or deselected (false)
 * @param selectedPillsCount - Current count of selected pills BEFORE this selection
 * @returns true if selection is allowed, false otherwise
 */
function validatePillSelection(
  libraryElement: Element,
  pillElement: Element | null,
  isSelected: boolean,
  selectedPillsCount: number
): boolean {
  // Always allow deselection
  if (!isSelected) {
    return true;
  }

  // Check if filtering is disabled (fewer than 9 results: 9 = perPage (8) + 1, matching server-side logic at line 998)
  const isFilteringDisabled = libraryElement.getAttribute('data-disable-filtering') === 'true';
  if (isFilteringDisabled) {
    // Prevent selection when filtering is disabled
    if (pillElement) {
      pillElement.setAttribute('aria-selected', 'false');
    }
    return false;
  }

  // Enforce tag limit: selectedPillsCount represents count BEFORE this selection
  // Reject if we're at or above the limit (prevent selecting a 4th tag when 3 are already selected)
  if (selectedPillsCount >= MAX_SELECTED_TAGS) {
    if (pillElement) {
      pillElement.setAttribute('aria-selected', 'false');
    }
    return false;
  }

  return true;
}

function getLibPageValueFromCurrentQuery(currentQuery: string): string | null {
  const test = /(libPage=(?<pageNum>(\d)+)+)/;
  const match = test.exec(currentQuery);
  return match && match.groups && match.groups.pageNum ? match.groups.pageNum : null;
}

function getLibCategoryValueFromCurrentQuery(currentQuery: string): string | null {
  const libCategoryTest = /(libCategory=(?<cat>(\w|\-|\d|\,)+)+)/;
  const match = libCategoryTest.exec(currentQuery);
  return match && match.groups && match.groups.cat ? match.groups.cat : null;
}

function getLibSubCategoriesValueFromCurrentQuery(currentQuery: string): string | null {
  const libSubCategoryTest = /(libSubCategory=(?<subcats>(\w|\-|\d|\,)+)+)/;
  const match = libSubCategoryTest.exec(currentQuery);
  return match && match.groups && match.groups.subcats ? match.groups.subcats : null;
}

function updateLibraryParams(paramName: string, newVal: string): void {
  const currentQuery = window.location.search.toString();
  const cat =
    paramName === 'libCategory' ? newVal : getLibCategoryValueFromCurrentQuery(currentQuery);
  const subcats =
    paramName === 'libSubCategory'
      ? newVal
      : getLibSubCategoriesValueFromCurrentQuery(currentQuery);
  const page = paramName === 'libPage' ? newVal : getLibPageValueFromCurrentQuery(currentQuery);

  let params: string[] = [];

  if (cat) {
    params.push(`libCategory=${cat}`);
  }

  if (subcats && paramName !== 'libCategory') {
    params.push(`libSubCategory=${subcats}`);
  }

  if (paramName === 'libPage') {
    params.push(`libPage=${page}`);
  }

  const newurl = window.location.pathname + '?' + params.join('&');
  window.history.pushState({ path: newurl }, '', newurl);
}

function pageIncrementedQueryString(currentPage: number): string {
  const currentQuery = window.location.search || '';
  // No existing query params
  if (!currentQuery || currentQuery === '?') {
    return `?libPage=${currentPage + 1}`;
  }
  // Replace existing libPage with next
  if (/([?&])libPage=\d+/.test(currentQuery)) {
    return currentQuery.replace(/([?&])libPage=\d+/, `$1libPage=${currentPage + 1}`);
  }
  // Append to existing query that already starts with '?'
  if (currentQuery.startsWith('?')) {
    return `${currentQuery}&libPage=${currentPage + 1}`;
  }
  // Fallback
  return `?libPage=${currentPage + 1}`;
}

// Move refreshTags function outside to make it globally available
function refreshTags(): void {
  const library = document.querySelector('ybc-library') as HTMLElement | null;
  if (!library) return;

  const currentQuery = window.location.search.toString();
  const postTypeRaw = (library.dataset.postType as string) || '';
  const postType = postTypeRaw.trim() || 'post';
  const taxonomy = library.dataset.taxonomy as string;
  const category = getLibCategoryValueFromCurrentQuery(currentQuery);
  const effectiveCategory = (category && category.trim()) || 'all';
  const xhr = new XMLHttpRequest();
  const path = `/wp-json/yubicool/v1/category-tags`;
  let params: string[] = [];

  // Add post type
  if (postType) {
    params.push(`post_type=${postType}`);
  }

  // Add category filter if applicable
  if (category && category !== 'all') {
    params.push(`category=${category}`);
    params.push(`taxonomy=${taxonomy}`);
  }

  xhr.open('GET', `${path}?${params.join('&')}`, true);

  // Get the pill bottle element
  const pillBottle = library.querySelector('ybc-pill-bottle') as HTMLElement | null;

  // Show loading state for pills
  if (library && typeof (library as any).showPillsLoading === 'function') {
    (library as any).showPillsLoading();
  }

  xhr.onreadystatechange = function () {
    if (this.readyState === 4 && this.status === 200) {
      try {
        const tags = JSON.parse(this.responseText);

        // Define tag interface
        interface TagObject {
          term_id: string;
          name: string;
          slug: string;
          selected: boolean;
        }

        // Use the ybc-library component's updatePills method
        if (library) {
          // Convert tags to the format expected by updatePills
          const pillsData = tags.map((tag: TagObject) => ({
            id: tag.term_id,
            name: tag.name,
            slug: tag.slug,
            selected: tag.selected,
          }));
          (library as any).updatePills(pillsData);
        }

        if (pillBottle && tags.length === 0) {
          // No tags available - handle this case directly
          while (pillBottle.firstChild) {
            pillBottle.removeChild(pillBottle.firstChild);
          }

          // Add a message as a placeholder
          const noTagsMessage = document.createElement('div');
          noTagsMessage.textContent = 'No tags available for this selection';
          noTagsMessage.setAttribute('slot', 'pills');
          pillBottle.appendChild(noTagsMessage);
        }
      } catch (e) {
        // Handle error using component method
        if (library && typeof (library as any).showPillsError === 'function') {
          (library as any).showPillsError('Error loading tags');
        }
      }
    } else if (this.readyState === 4) {
      // Handle HTTP error using component method
      if (library && typeof (library as any).showPillsError === 'function') {
        (library as any).showPillsError('Error loading tags');
      }
    }
  };

  xhr.send();
}

function fetchNewData(page?: number, libEl?: HTMLElement): void {
  const library =
    (libEl as HTMLElement | null) || (document.querySelector('ybc-library') as HTMLElement | null);
  if (!library) {
    return;
  }
  const resultsContainer = library.querySelector('ybc-feed.library-results') as HTMLElement | null;
  const paginationButton = library.querySelector('#paginationButton') as HTMLAnchorElement | null;
  const pillBottle = library.querySelector('ybc-pill-bottle') as HTMLElement | null;
  const currentQuery = window.location.search.toString();
  const postTypeRaw = (library.dataset.postType as string) || '';
  const postType = postTypeRaw.trim() || 'post';
  const taxonomy = library.dataset.taxonomy as string;
  const category = getLibCategoryValueFromCurrentQuery(currentQuery);
  const effectiveCategory = (category && category.trim()) || 'all';
  const subCategories = getLibSubCategoriesValueFromCurrentQuery(currentQuery);
  const xhr = new XMLHttpRequest();
  const path = `/wp-json/yubicool/v1/library/${postType}`;
  let params: string[] = [];

  if (taxonomy) {
    params.push(`libTaxonomy=${taxonomy}`);
  }

  if (effectiveCategory) {
    params.push(`libCategory=${effectiveCategory}`);
  }

  if (subCategories) {
    params.push(`libSubCategory=${subCategories}`);
  }

  if (page) {
    params.push(`libPage=${page}`);
    paginationButton?.classList.add('ybc-hidden');
    // Insert spinner after the pagination button
    paginationButton?.insertAdjacentHTML(
      'afterend',
      "<div class='yubicool-spinner' id='yubicool-pagination-spinner'></div>"
    );
  } else {
    if (resultsContainer) {
      resultsContainer.innerHTML = "<div class='yubicool-spinner'></div>";
    }
  }

  const url = `${path}?${params.join('&')}`;
  xhr.open('GET', url, true);

  xhr.onreadystatechange = function () {
    if (this.readyState === 4 && this.status === 200) {
      if (resultsContainer) {
        const resp = JSON.parse(this.responseText);
        const meta = resp['meta'] || {};
        // Render/append feed-card HTML directly into the ybc-feed container
        const html = resp['html'] || '';
        if (!page) {
          resultsContainer.innerHTML = html;
        } else {
          document.getElementById('yubicool-pagination-spinner')?.remove();
          resultsContainer.insertAdjacentHTML('beforeend', html);
        }

        // Update total results if provided
        let totalResults: number | undefined;
        const postCountRaw = resp['results']?.['post_count'];
        if (postCountRaw !== undefined && postCountRaw !== null) {
          totalResults = parseInt(postCountRaw, 10);
        }

        if (resultsContainer.dataset) {
          resultsContainer.dataset.totalResults =
            totalResults !== undefined ? String(totalResults) : '';
        }

        // Disable filtering when there are fewer results than would fill a second page.
        // This threshold (9) corresponds to perPage + 1 on the server side.
        const shouldDisableFiltering = typeof totalResults === 'number' ? totalResults < 9 : true;
        if (library) {
          library.setAttribute('data-disable-filtering', shouldDisableFiltering ? 'true' : 'false');
        }

        // Use REST meta to update URL and button state
        if (typeof meta['page'] === 'number') {
          updateLibraryParams('libPage', String(meta['page']));
        }

        if (paginationButton) {
          // Handle pagination button based on whether there are more pages
          // The web component handles the disabled state based on data-disable-filtering
          if (meta['hasMore']) {
            const currentPage = typeof meta['page'] === 'number' ? meta['page'] : 1;
            const newPaginationButtonHref = pageIncrementedQueryString(currentPage);
            paginationButton.setAttribute('href', newPaginationButtonHref);
            paginationButton.classList.remove('ybc-hidden');
          } else {
            // No more pages - remove href
            paginationButton.removeAttribute('href');
            paginationButton.classList.remove('ybc-hidden');
            document.getElementById('yubicool-pagination-spinner')?.remove();
          }
        }
      }
    }
  };

  xhr.send();

  // Set global handler for ybc-library category selection
  interface YbcLibraryCategoryHandlerArgs {
    category: string | null;
    element: HTMLElement;
    library: HTMLElement;
  }

  (window as any).ybcLibraryCategoryHandler = function ({
    category,
    library,
  }: YbcLibraryCategoryHandlerArgs) {
    if (category) {
      // Category handler triggered
      // Clear any selected tags when category changes
      updateLibraryParams('libSubCategory', '');

      // Update category and fetch new data
      updateLibraryParams('libCategory', (category && category.trim()) || 'all');
      fetchNewData(undefined, library);

      // Refresh tags for the new category
      refreshTags();
    }
  };

  // Set global handler for ybc-library pill selection
  interface YbcLibraryPillHandlerArgs {
    pill: HTMLElement;
    selected: boolean;
    library: HTMLElement;
  }

  (window as any).ybcLibraryPillHandler = function ({
    pill,
    selected,
    library,
  }: YbcLibraryPillHandlerArgs) {
    const tag = pill.getAttribute('data-toggles');
    if (tag) {
      let current = getLibSubCategoriesValueFromCurrentQuery(window.location.search) || '';
      let tags = current ? current.split(',') : [];
      if (selected) {
        if (!tags.includes(tag)) tags.push(tag);
      } else {
        tags = tags.filter((t) => t !== tag);
      }
      updateLibraryParams('libSubCategory', tags.filter(Boolean).join(','));
      fetchNewData(undefined, library);
    }
  };

  // Removed library-category-updated listener to prevent conflicts with category-selected handler

  // Update results on browser navigation (back/forward)
  window.addEventListener('popstate', () => {
    fetchNewData();
  });
}

document.addEventListener('DOMContentLoaded', function () {
  try {
    const library = document.querySelector('ybc-library');
    const paginationButton = document.querySelector('#paginationButton');

    if (!library) {
      return;
    }

    // Flag to prevent recursive calls
    let isUpdating = false;

    // Listen for category selection events from ybc-library component
    library.addEventListener('category-selected', (e: any) => {
      if (isUpdating) {
        return;
      }
      isUpdating = true;

      try {
        const categorySlug = e.detail.categorySlug;
        updateLibraryParams('libCategory', (categorySlug && categorySlug.trim()) || 'all');
        // Update both cards and tags when category changes
        fetchNewData(undefined, e.currentTarget as HTMLElement);
        refreshTags();
      } finally {
        // Reset flag after a short delay to allow operations to complete
        setTimeout(() => {
          isUpdating = false;
        }, 100);
      }
    });

    // Listen for tags-requested events from ybc-library component
    library.addEventListener('tags-requested', (e: any) => {
      // Call refreshTags directly to update available tags for the selected category
      refreshTags();
    });

    // Listen for pill selection events from ybc-library component
    library.addEventListener('pill-selected', (e: any) => {
      if (isUpdating) {
        return;
      }

      try {
        isUpdating = true;

        // Use the library component's helper method to get selected pills
        if (library && typeof (library as any).getSelectedPills === 'function') {
          const selectedPills = (library as any).getSelectedPills();

          // getSelectedPills() always includes the pill being toggled, so subtract 1 to get count before toggle
          const pillsCountForValidation = selectedPills.length - 1;
          if (
            !validatePillSelection(
              library,
              e.detail.pill,
              e.detail.selected,
              pillsCountForValidation
            )
          ) {
            isUpdating = false;
            return;
          }

          const enabledPills = selectedPills.map((pill: any) => pill.slug);
          updateLibraryParams('libSubCategory', enabledPills.join(','));
          // Trigger fetch after updating tags
          fetchNewData(undefined, e.currentTarget as HTMLElement);
        }
      } finally {
        // Reset flag after a short delay to allow operations to complete
        setTimeout(() => {
          isUpdating = false;
        }, 100);
      }
    });

    // Fallback: Also listen for click events on pills and categories
    library.addEventListener('click', (e) => {
      if (isUpdating) {
        return;
      }

      const target = e.target as Element;

      // If this is a pill click, prevent default navigation and trigger fetch as a fallback
      if (target.closest('ybc-pill') || target.tagName.toLowerCase() === 'ybc-pill') {
        e.preventDefault();
        isUpdating = true;
        try {
          // Allow component to update aria-selected before reading state
          setTimeout(() => {
            try {
              const libEl = e.currentTarget as HTMLElement;
              if (libEl && typeof (libEl as any).getSelectedPills === 'function') {
                const selectedPills = (libEl as any).getSelectedPills();
                const pillElement = target.closest('ybc-pill') as HTMLElement;
                const isPillSelected = pillElement?.getAttribute('aria-selected') === 'true';

                // getSelectedPills() always includes the pill being toggled, so subtract 1 to get count before toggle
                const pillsCountForValidation = selectedPills.length - 1;
                if (
                  !validatePillSelection(
                    libEl,
                    pillElement,
                    isPillSelected,
                    pillsCountForValidation
                  )
                ) {
                  isUpdating = false;
                  return;
                }

                const enabledPills = selectedPills.map((pill: any) => pill.slug);
                updateLibraryParams('libSubCategory', enabledPills.join(','));
                fetchNewData(undefined, libEl);
              }
            } finally {
              setTimeout(() => {
                isUpdating = false;
              }, 100);
            }
          }, 0);
        } catch (err) {
          setTimeout(() => {
            isUpdating = false;
          }, 100);
        }
        return;
      }

      // Handle category clicks - be more specific to avoid conflicts
      if (
        target.classList.contains('yubicool-library-category') ||
        (target.hasAttribute('data-slug') && !target.hasAttribute('data-toggles')) ||
        (target.closest('[data-slug]') && !target.closest('ybc-pill'))
      ) {
        e.preventDefault(); // Prevent default navigation
        isUpdating = true;

        try {
          // Find the actual category element
          const categoryElement = target.closest('[data-slug]') || target;
          const categorySlug = (categoryElement as HTMLElement).dataset.slug || '';

          if (categorySlug) {
            updateLibraryParams('libCategory', categorySlug);
            fetchNewData();
          }
        } finally {
          // Reset flag after a short delay to allow operations to complete
          setTimeout(() => {
            isUpdating = false;
          }, 100);
        }
      }
    });

    // Prefer event from component over direct anchor binding
    library.addEventListener('pagination-requested', (e: any) => {
      const currentPageStr = getLibPageValueFromCurrentQuery(window.location.search) || '1';
      const currentPage = parseInt(currentPageStr);
      const nextPage = currentPage + 1;
      // Mark handled to avoid delegated click fallback double-fetch
      (window as any).__ybcPaginationHandledTs = performance.now();
      try {
        fetchNewData(nextPage, e.currentTarget as HTMLElement);
      } catch (err) {
        // swallow
      }
    });

    // Fallback: document-level listener in case event dispatch originates from a different
    // ybc-library instance than the one we bound above
    document.addEventListener(
      'pagination-requested',
      (e: any) => {
        const currentPageStr = getLibPageValueFromCurrentQuery(window.location.search) || '1';
        const currentPage = parseInt(currentPageStr) || 1;
        const nextPage = currentPage + 1;
        try {
          const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
          const libNode = path.find((n: any) => n && n.tagName === 'YBC-LIBRARY') as
            | HTMLElement
            | undefined;
          fetchNewData(nextPage, libNode as HTMLElement | undefined);
        } catch (err) {
          // swallow
        }
      },
      { capture: true }
    );

    // Debug: delegated click log for the CTA to verify click path and attributes
    document.addEventListener('click', (e) => {
      const anchor = (e.target as Element).closest('#paginationButton') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        const disabled = anchor.getAttribute('aria-disabled');
        // Fallback: if enabled and has href, prevent default and fetch next page
        const handledTs = (window as any).__ybcPaginationHandledTs as number | undefined;
        const recentlyHandled =
          typeof handledTs === 'number' && performance.now() - handledTs < 200;
        if (!e.defaultPrevented && !recentlyHandled && href && disabled !== 'true') {
          e.preventDefault();
          const match = /[?&]libPage=(\d+)/.exec(href);
          const currentPageStr = getLibPageValueFromCurrentQuery(window.location.search) || '1';
          const inferredNext = (parseInt(currentPageStr, 10) || 1) + 1;
          const nextPage = match ? parseInt(match[1], 10) : inferredNext;
          (window as any).__ybcPaginationHandledTs = performance.now();
          const libEl = anchor.closest('ybc-library') as HTMLElement | null;
          fetchNewData(nextPage, libEl || undefined);
        }
      }
    });

    // Prevent full-page navigation on pill clicks; rely on pill-selected handler instead
    document.addEventListener(
      'click',
      (e) => {
        const anyEvent = e as any;
        const path: any[] =
          typeof anyEvent.composedPath === 'function' ? anyEvent.composedPath() : [];
        const inLibrary = path.some((n) => n && n.tagName === 'YBC-LIBRARY');
        const pillEl = path.find((n) => n && n.tagName === 'YBC-PILL');
        if (inLibrary && pillEl) {
          // If a pill is clicked inside the library, prevent default navigation
          e.preventDefault();
        }
      },
      { capture: true }
    );

    // No debug hooks in production

    // IMPORTANT: Preserve SSR on initial load.
    // If SSR already rendered results (or libPage is present), do NOT auto-fetch,
    // otherwise we'd replace SSR content and reset libPage to 1.
    const initialPage = getLibPageValueFromCurrentQuery(window.location.search);
    const hasSSRResults = !!document.querySelector(
      'ybc-library ybc-feed.library-results li, ybc-library ybc-feed.library-results ybc-feed-card, ybc-library ybc-feed.library-results ybc-card'
    );
    if (hasSSRResults) {
      // Ensure the CTA has a valid href so the component will dispatch
      const libraryEl = document.querySelector('ybc-library') as HTMLElement | null;
      const paginationBtn = libraryEl?.querySelector(
        '#paginationButton'
      ) as HTMLAnchorElement | null;
      if (paginationBtn) {
        const currentPageStr = (initialPage as string) || '1';
        const currentPageNum = parseInt(currentPageStr) || 1;
        let nextHref = pageIncrementedQueryString(currentPageNum);
        // Normalize to avoid leading '&' which creates an invalid path segment
        if (nextHref.startsWith('&')) {
          nextHref = `?${nextHref.slice(1)}`;
        }
        paginationBtn.setAttribute('href', nextHref);
        paginationBtn.classList.remove('ybc-hidden');
        paginationBtn.removeAttribute('aria-disabled');
      } else {
        // no-op
      }
    } else if (initialPage) {
      const pageNum = parseInt(initialPage);
      fetchNewData(pageNum);
    } else {
      fetchNewData();
    }
  } catch (e) {
    // swallow
  }
});

export {};
